import AppPagination from "@app/common/components/AppPagination";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import { Template } from "@app/common/components/Template";
import { TextField } from "@app/common/components/TextField";
import { formattedDataSelect, formatLastUpdate } from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import routes from "@app/config/routes";

import {
  useGetCompanyClientList,
  useGetContractList,
  useGetStatusList
} from "@app/services/common/hooks";

import { useGetSupportConsultList } from "@app/services/SupportsConsult/hooks";
import { SupportConsultFilter, SupportConsultResponse } from "@app/services/SupportsConsult/types";
import { ActionIcon, Grid, Select, Table, Text, TextInput } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const DispensarySupportFiles = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<SupportConsultFilter>({
    startDate: "",
    endDate: "",
    corporateClient: null,
    contractId: null,
    status: null,
    search: "",
    page: 1
  });

  const { data: supportList, refetch } = useGetSupportConsultList({
    ...filters,
    perpage: 10
  });

  const { data: companyList } = useGetCompanyClientList({
    status: "enabled",
    eaccount: GCidAccount()
  });

  const { data: contractList } = useGetContractList(
    { corporateClient: Number(filters.corporateClient), slim: 1 },
    filters.corporateClient !== null
  );

  const { data: statusList } = useGetStatusList({
    staFunctionality: "dispensary_supports_reviews"
  });

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    refetch();
  };

  const renderFiltersHeader = () => {
    return (
      <Grid
        align="flex-end"
        mb="lg"
        w="100%"
        columns={10}
        data-testid="dispensarysupportfiles-filters"
      >
        <Grid.Col span={2}>
          <TextInput
            data-testid="dispensarysupportfiles-start-date-input"
            label="Fecha desde"
            type="date"
            placeholder="Escribir..."
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                startDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            data-testid="dispensarysupportfiles-end-date-input"
            label="Fecha hasta"
            type="date"
            min={filters.startDate}
            placeholder="Escribir..."
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                endDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Select
            data-testid="dispensarysupportfiles-company-select"
            label="Empresa / Cliente"
            data={formattedDataSelect(companyList?.results ?? [], "name", "id")}
            searchable
            placeholder="Seleccionar..."
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                corporateClient: value ?? "",
                contractId: ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Select
            data-testid="dispensarysupportfiles-contract-select"
            label="Contrato"
            searchable
            data={formattedDataSelect(contractList?.results ?? [], "contractName", "id")}
            placeholder="Seleccionar..."
            value={filters.contractId}
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                contractId: value ?? ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Select
            data-testid="dispensarysupportfiles-status-select"
            label="Estado"
            searchable
            data={formattedDataSelect(statusList?.results ?? [], "description", "id")}
            placeholder="Seleccionar..."
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                status: value ?? ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextField
            placeholder="Escribe aquí para buscar..."
            variant="filled"
            className="flex-grow-1 me-4"
            name="search"
            data-testid="dispensarysupportfiles-search-input"
            onChange={({ target }) =>
              setFilters((state) => ({
                ...state,
                search: target.value
              }))
            }
          />
        </Grid.Col>

        <Grid.Col span={2}>
          <FilterSearchButton />
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: SupportConsultResponse, index: number) => {
    const date = (option?.createdAt ?? "").split("T")[0].replaceAll("/", "-");
    return (
      <Table.Tr key={option?.disId} data-testid={`dispensarysupportfiles-row-${index}`}>
        <Table.Td style={{ textWrap: "nowrap" }}>{date}</Table.Td>

        <Table.Td style={{ textWrap: "nowrap" }}>{option?.patient}</Table.Td>

        <Table.Td ta={"start"} style={{ textWrap: "nowrap" }}>
          {option?.patientDocument}
        </Table.Td>
        <Table.Td ta={"start"} style={{ minWidth: "300px" }}>
          {option?.corporateClient}
        </Table.Td>
        <Table.Td ta={"start"} style={{ minWidth: "300px" }}>
          {option?.contract}
        </Table.Td>
        <Table.Td ta={"center"}>
          <div className="badge-light-blue">{option?.service}</div>
        </Table.Td>
        <Table.Td ta={"center"}>{option?.supports}</Table.Td>
        <Table.Td ta={"start"} style={{ textWrap: "nowrap" }}>
          {option?.assessor}
        </Table.Td>
        <Table.Td ta={"start"} style={{ minWidth: "260px" }}>
          {formatLastUpdate(option?.updatedAt, option?.updatedBy)}
        </Table.Td>
        <Table.Td ta={"center"}>
          <Text fz="xs" ml="xs" flex={1} fw={"inherit"}>
            <div
              className="badge-base"
              style={{ color: option.status?.fontColor, backgroundColor: option.status?.bgColor }}
            >
              {option.status?.description}
            </div>
          </Text>
        </Table.Td>
        <Table.Td ta="end">
          <ActionIcon
            variant="subtle"
            data-testid={`dispensarysupportfiles-detail-button-${index}`}
          >
            <IconChevronRight
              onClick={() =>
                navigate(routes.getDispensarySupportFilesDetail, { state: { id: option.disId } })
              }
            />
          </ActionIcon>
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <div style={{ overflowX: "auto" }} data-testid="dispensarysupportfiles-table-container">
        <Table className="biowel-table" data-testid="dispensarysupportfiles-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th ta={"center"}>Fecha</Table.Th>
              <Table.Th>Paciente</Table.Th>
              <Table.Th ta={"start"}>Identificación</Table.Th>
              <Table.Th ta={"start"}>Empresa</Table.Th>
              <Table.Th ta={"start"}>Contrato</Table.Th>
              <Table.Th ta={"start"}>Servicio</Table.Th>
              <Table.Th ta={"center"}>Documentos</Table.Th>
              <Table.Th ta={"center"}>Asesor</Table.Th>
              <Table.Th ta={"start"} style={{ minWidth: "260px" }}>
                Última Actualización
              </Table.Th>
              <Table.Th ta={"center"}>Estado</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {supportList?.results?.length ? (
              supportList?.results?.map((item, index) => renderRow(item, index))
            ) : (
              <Table.Tr>
                <Table.Td ta="center" colSpan={12} data-testid="dispensarysupportfiles-no-data">
                  No hay información para mostrar.
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </div>
    );
  };

  return (
    <Template title="Auditoría de soportes">
      <form onSubmit={handleSubmitFilters} data-testid="dispensarysupportfiles-form">
        {renderFiltersHeader()}
      </form>

      {renderTable()}

      <AppPagination
        total={supportList?.rowTotal || 0}
        value={filters.page || 1}
        withDetails
        onChange={(value) => setFilters((state) => ({ ...state, page: value }))}
        data-testid="dispensarysupportfiles-pagination"
      />
    </Template>
  );
};

export default DispensarySupportFiles;